/** Project materials library (K5 UI). Kinds + honest index status — never fake "indexed". */

export const MATERIAL_KINDS = ["datasheet", "paper", "requirement", "note", "bom"] as const;
export type MaterialKind = (typeof MATERIAL_KINDS)[number];

export function isMaterialKind(value: unknown): value is MaterialKind {
  return typeof value === "string" && (MATERIAL_KINDS as readonly string[]).includes(value);
}

export type MaterialRow = {
  id: string;
  project_id?: string;
  kind: string;
  filename: string;
  r2_key?: string | null;
  content_type?: string | null;
  byte_size?: number | null;
  mpn?: string | null;
  ai_search_item_id?: string | null;
  parser?: string | null;
  created_by?: string | null;
  created_at?: string | null;
  deleted_at?: string | null;
  /** Present on upload responses; GET list is D1 columns only. */
  status?: string | null;
  needs_ocr?: boolean | null;
};

export type MaterialUploadResult = {
  id?: string;
  r2_key?: string;
  ai_search_item_id?: string | null;
  parser?: string | null;
  needs_ocr?: boolean;
  status?: string;
  ok?: boolean;
  error?: string;
  message?: string;
};

export type MaterialRowStatus = {
  label: string;
  needs_ocr: boolean;
  indexed: boolean;
};

/**
 * Honest status from a materials row.
 * Indexed only when `ai_search_item_id` is set — never report "indexed" from status/parser alone.
 */
export function materialRowStatus(
  row: Pick<MaterialRow, "status" | "needs_ocr" | "parser" | "ai_search_item_id">,
): MaterialRowStatus {
  const itemId = typeof row.ai_search_item_id === "string" ? row.ai_search_item_id.trim() : "";
  const indexed = itemId.length > 0;
  const statusRaw = typeof row.status === "string" ? row.status.trim() : "";

  if (indexed) {
    return {
      label: statusRaw || "indexed",
      needs_ocr: false,
      indexed: true,
    };
  }

  const needsOcr =
    row.needs_ocr === true ||
    statusRaw === "needs_ocr" ||
    row.parser == null ||
    row.parser === "";

  if (needsOcr) {
    return { label: statusRaw && statusRaw !== "indexed" ? statusRaw : "needs_ocr", needs_ocr: true, indexed: false };
  }

  if (statusRaw && statusRaw !== "indexed") {
    return { label: statusRaw, needs_ocr: false, indexed: false };
  }

  return { label: "not_indexed", needs_ocr: false, indexed: false };
}

export function materialSearchHits(body: unknown): unknown[] {
  if (Array.isArray(body)) return body;
  if (!body || typeof body !== "object") return [];
  const rec = body as Record<string, unknown>;
  if (Array.isArray(rec.data)) return rec.data;
  if (Array.isArray(rec.results)) return rec.results;
  return [];
}

export function materialHitText(hit: unknown): string {
  if (hit == null) return "";
  if (typeof hit === "string") return hit;
  if (typeof hit !== "object") return String(hit);
  const rec = hit as Record<string, unknown>;
  if (typeof rec.text === "string" && rec.text.trim()) return rec.text;
  if (typeof rec.content === "string" && rec.content.trim()) return rec.content;
  try {
    return JSON.stringify(hit);
  } catch {
    return String(hit);
  }
}

export function projectHasHardwareRevision(hw: { revisions?: unknown[] | null } | null | undefined): boolean {
  return Array.isArray(hw?.revisions) && hw.revisions.length >= 1;
}
